import type { LifecycleStageStep } from '../../../components/LifecycleStageTracker';
import {
  formatCount,
  formatDay,
  formatDaysLate,
  pluralize,
  trackerExceptionLabel,
  trackerStepKey,
  trackerSteps,
  type PurchaseOrderDocument,
  type ScanCountLine,
  type ScanCountSubmission,
} from '@/domain/warehouse';
import type {
  PurchaseOrderActions,
  PurchaseOrderReceiveLine,
  PurchaseOrderRecord,
  PurchaseOrderRecordLine,
  PurchaseOrderTimelineEntry,
} from '@/data/purchaseOrderRecord';
import type { PurchaseOrderAssetUnitsLine } from '../../../components/procurement/PurchaseOrderAssetUnitsDialog';
import {
  purchaseLineName,
  purchaseLineOpenQty,
  purchaseLineUseLabel,
} from '../../../components/procurement/PurchaseOrderLineLabels';

/** Record sections, in tab order. `/transfers/purchase-orders/:poId/<section>`. */
export const PURCHASE_ORDER_SECTIONS = ['lines', 'receiving', 'details', 'history'] as const;
export type PurchaseOrderSection = (typeof PURCHASE_ORDER_SECTIONS)[number];

export function parsePurchaseOrderSection(raw: string | null | undefined): { section: PurchaseOrderSection; known: boolean } {
  const value = String(raw ?? '').split('/')[0].trim().toLowerCase();
  const match = PURCHASE_ORDER_SECTIONS.find((section) => section === value);
  return match ? { section: match, known: true } : { section: 'lines', known: value.length === 0 };
}

export function purchaseOrderHref(poId: string, section?: PurchaseOrderSection, search?: string): string {
  const base = `/transfers/purchase-orders/${encodeURIComponent(poId)}${section ? `/${section}` : ''}`;
  return search ? `${base}?${search}` : base;
}

/** Actions a viewer can take: the record allows them and the viewer has edit on purchase orders. */
export type PurchaseOrderActionKey = 'receive' | 'submit' | 'acknowledge' | 'close' | 'cancel' | 'set_expected';

export function actionAllowed(actions: PurchaseOrderActions, key: PurchaseOrderActionKey, canEdit: boolean): boolean {
  if (!canEdit) return false;
  switch (key) {
    case 'receive':
      return actions.receive.allowed;
    case 'submit':
      return actions.submit.allowed;
    case 'acknowledge':
      return actions.acknowledge.allowed;
    case 'close':
      return actions.close.allowed;
    case 'cancel':
      return actions.cancel.allowed;
    case 'set_expected':
    default:
      return actions.setExpected.allowed;
  }
}

/** Lines that can be received: not cancelled and not cross-rentals (those receive as Subrentals). */
export function receivableLines(lines: readonly PurchaseOrderRecordLine[]): PurchaseOrderRecordLine[] {
  return lines.filter((line) => line.status !== 'cancelled' && line.disposition !== 'cross_rental');
}

export function openUnits(lines: readonly PurchaseOrderRecordLine[]): number {
  return receivableLines(lines).reduce((sum, line) => sum + purchaseLineOpenQty(line), 0);
}

/** One primary action at a time, mirrored in the next-step banner and the phone action bar. */
export function primaryPurchaseOrderAction(record: PurchaseOrderRecord, canEdit: boolean): PurchaseOrderActionKey | null {
  const { actions, lines } = record;
  if (actionAllowed(actions, 'submit', canEdit)) return 'submit';
  if (actionAllowed(actions, 'receive', canEdit) && openUnits(lines) > 0) return 'receive';
  if (actionAllowed(actions, 'acknowledge', canEdit)) return 'acknowledge';
  if (actionAllowed(actions, 'close', canEdit) && openUnits(lines) === 0) return 'close';
  return null;
}

export const ACTION_LABELS: { readonly [key in PurchaseOrderActionKey]: string } = {
  receive: 'Receive items',
  submit: 'Submit order',
  acknowledge: 'Mark acknowledged',
  close: 'Close order',
  cancel: 'Cancel order',
  set_expected: 'Change expected date',
};

/** "2 × Shure ULXD2/B58 handheld transmitter, 12 × Gaffer tape and 2 more lines." */
export function openItemsSentence(lines: readonly PurchaseOrderRecordLine[], limit = 3): string {
  const open = receivableLines(lines).filter((line) => purchaseLineOpenQty(line) > 0);
  if (!open.length) return '';
  const parts = open.slice(0, limit).map((line) => `${formatCount(purchaseLineOpenQty(line))} × ${purchaseLineName(line)}`);
  if (open.length > limit) parts.push(pluralize(open.length - limit, 'more line'));
  const sentence = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  return `${sentence}.`;
}

export type PurchaseOrderNextStep = {
  tone: 'info' | 'ok' | 'warn';
  lead: string;
  detail: string | null;
  actions: PurchaseOrderActionKey[];
};

/** The one-line "what happens next" under the header. */
export function purchaseOrderNextStep(record: PurchaseOrderRecord, canEdit: boolean): PurchaseOrderNextStep {
  const { purchaseOrder: po, lines, totals, actions } = record;
  const allowed = (keys: PurchaseOrderActionKey[]) => keys.filter((key) => actionAllowed(actions, key, canEdit));
  const open = openUnits(lines);
  const status = po.status.trim().toLowerCase();
  if (status === 'cancelled') {
    return {
      tone: 'info',
      lead: 'This order was cancelled.',
      detail: totals.unitsReceived > 0
        ? `${pluralize(totals.unitsReceived, 'unit')} received before it was cancelled ${totals.unitsReceived === 1 ? 'stays' : 'stay'} in stock.`
        : null,
      actions: [],
    };
  }
  if (status === 'closed') {
    return {
      tone: 'ok',
      lead: 'Closed.',
      detail: `${formatCount(totals.unitsReceived)} of ${pluralize(totals.unitsOrdered, 'unit')} received.`,
      actions: [],
    };
  }
  if (status === 'draft') {
    return {
      tone: 'info',
      lead: 'Draft.',
      detail: 'Add the lines, then submit the order to the vendor.',
      actions: allowed(['submit', 'receive']),
    };
  }
  if (open > 0) {
    const items = openItemsSentence(lines);
    const late = formatDaysLate(po.daysLate);
    if (late) {
      const days = Math.trunc(po.daysLate ?? 0);
      return {
        tone: 'warn',
        lead: `${pluralize(open, 'unit')} ${open === 1 ? 'is' : 'are'} still due, ${days === 1 ? '1 day' : `${days} days`} past the expected date:`,
        detail: items,
        actions: allowed(['receive', 'set_expected']),
      };
    }
    const expected = po.expectedDay ? formatDay(po.expectedDay) : null;
    return {
      tone: 'info',
      lead: `${pluralize(open, 'unit')} ${open === 1 ? 'is' : 'are'} still due${expected ? `, expected ${expected}` : ''}:`,
      detail: items,
      actions: allowed(status === 'submitted' ? ['receive', 'acknowledge'] : expected ? ['receive'] : ['receive', 'set_expected']),
    };
  }
  return {
    tone: 'ok',
    lead: 'Everything is in.',
    detail: 'Close the order once the vendor’s bill is settled.',
    actions: allowed(['close']),
  };
}

function entryStatus(entry: PurchaseOrderTimelineEntry): string | null {
  const details = entry.details;
  const candidates = [details?.to, details?.status, details?.new_status, details?.action, entry.kind];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      const value = candidate.trim().toLowerCase();
      if (value === 'acknowledge') return 'acknowledged';
      if (value === 'submit') return 'submitted';
      if (value === 'close') return 'closed';
      return value;
    }
  }
  return null;
}

/** The earliest timeline entry for each status the order reached. */
function statusDates(timeline: readonly PurchaseOrderTimelineEntry[]): Map<string, string> {
  const dates = new Map<string, string>();
  for (const entry of timeline) {
    const status = entryStatus(entry);
    if (!status || !entry.at) continue;
    const known = dates.get(status);
    if (!known || entry.at < known) dates.set(status, entry.at);
  }
  return dates;
}

function shortDay(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export type PurchaseOrderTracker = {
  steps: LifecycleStageStep[];
  currentStepKey: string | null;
  currentStatusLabel: string;
  exceptionLabel: string | null;
};

/** Draft · Submitted · Acknowledged · Partially received (42 of 58) · Received · Closed. */
export function purchaseOrderTracker(record: PurchaseOrderRecord, statusLabel: string): PurchaseOrderTracker {
  const { purchaseOrder: po, totals, timeline } = record;
  const currentStepKey = trackerStepKey('purchaseOrder', po.status);
  const steps = trackerSteps('purchaseOrder');
  const currentIndex = steps.findIndex((step) => step.key === currentStepKey);
  const dates = statusDates(timeline);
  const units = `${formatCount(totals.unitsReceived)} of ${formatCount(totals.unitsOrdered)}`;
  return {
    steps: steps.map((step, index) => {
      const reached = currentIndex >= 0 && index <= currentIndex;
      let detail: string | null = null;
      if (step.key === 'partially_received' && step.key === currentStepKey) detail = units;
      else if (step.key === 'received' && step.key === currentStepKey) detail = units;
      else if (reached) {
        const at = dates.get(step.key) ?? (step.key === 'submitted' ? po.orderedAt : null);
        detail = shortDay(at);
      }
      return { key: step.key, label: step.label, targetStatus: step.statuses[0] ?? step.key, detail };
    }),
    currentStepKey,
    currentStatusLabel: statusLabel,
    exceptionLabel: trackerExceptionLabel('purchaseOrder', po.status),
  };
}

export type LineFilter = 'all' | 'open' | 'received';

export function lineMatchesFilter(line: PurchaseOrderRecordLine, filter: LineFilter): boolean {
  if (filter === 'all') return true;
  const open = purchaseLineOpenQty(line) > 0;
  if (filter === 'open') return open;
  return !open && line.status !== 'cancelled';
}

export function lineFilterCounts(lines: readonly PurchaseOrderRecordLine[]): { [key in LineFilter]: number } {
  return {
    all: lines.length,
    open: lines.filter((line) => lineMatchesFilter(line, 'open')).length,
    received: lines.filter((line) => lineMatchesFilter(line, 'received')).length,
  };
}

/**
 * The Receive dialog's lines: every receivable line, or only the chosen ones. Purchase-order units
 * do not exist yet, so every line counts by quantity (a scan of the SKU counts one unit); the
 * barcodes of serialized units are asked for after the count (PurchaseOrderAssetUnitsDialog).
 */
export function purchaseOrderReceiveLines(
  lines: readonly PurchaseOrderRecordLine[],
  onlyIds: ReadonlySet<string> | null,
): ScanCountLine[] {
  return receivableLines(lines)
    .filter((line) => !onlyIds || onlyIds.has(line.id))
    .map((line) => ({
      id: line.id,
      name: purchaseLineName(line),
      sku: line.item?.code ?? undefined,
      tracking: 'quantity' as const,
      expected: Math.max(0, Math.round(line.qty)),
      alreadyDone: Math.max(0, Math.round(line.receivedQty)),
      codes: line.item?.code ? [line.item.code] : undefined,
    }));
}

export type PurchaseOrderReceivePlan = {
  /** Lines received by count alone. */
  quantityLines: PurchaseOrderReceiveLine[];
  /** Serialized lines: each counted unit needs a barcode before anything is received. */
  serialLines: PurchaseOrderAssetUnitsLine[];
  units: number;
};

/** Splits the Receive dialog's submission into count-only lines and serialized lines. */
export function planPurchaseOrderReceive(
  submission: ScanCountSubmission,
  lines: readonly PurchaseOrderRecordLine[],
): PurchaseOrderReceivePlan {
  const byId = new Map<string, PurchaseOrderRecordLine>();
  lines.forEach((line) => byId.set(line.id, line));
  const plan: PurchaseOrderReceivePlan = { quantityLines: [], serialLines: [], units: 0 };
  for (const item of submission) {
    const line = byId.get(item.lineId);
    const qty = Math.max(0, Math.round(Number(item.qty ?? item.serialIds?.length ?? 0)));
    if (!line || qty <= 0) continue;
    plan.units += qty;
    if (line.tracking === 'serial') {
      plan.serialLines.push({ lineId: line.id, name: purchaseLineName(line), sku: line.item?.code ?? null, qty });
    } else {
      plan.quantityLines.push({ poLineId: line.id, qty });
    }
  }
  return plan;
}

/** True while the viewer's last click can still open a file chooser (the browser's user activation). */
export function hasTransientActivation(): boolean {
  if (typeof navigator === 'undefined') return false;
  const activation = (navigator as Navigator & { userActivation?: { isActive?: boolean } }).userActivation;
  return Boolean(activation?.isActive);
}

/** A timestamp as the viewer's calendar day (`YYYY-MM-DD`), for formatDay. */
export function localDay(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) return timestamp;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Event-sale lines and the event codes they sell to ("2 event-sale lines for 0917061"). */
export function eventSaleSummary(record: PurchaseOrderRecord): { count: number; codes: string[] } {
  const sale = record.lines.filter((line) => line.disposition === 'event_sale' && line.status !== 'cancelled');
  const codes = Array.from(new Set(
    sale.map((line) => line.event?.code ?? record.purchaseOrder.event?.code ?? null).filter((code): code is string => Boolean(code)),
  ));
  return { count: sale.length, codes };
}

/** The printable document's data (domain/warehouse/documents/purchaseOrderHtml). */
export function purchaseOrderDocument(record: PurchaseOrderRecord): PurchaseOrderDocument {
  const { purchaseOrder: po } = record;
  return {
    docNo: po.docNo,
    status: po.status,
    isR2: po.isR2,
    kind: po.kind,
    vendorName: po.vendor?.name ?? null,
    vendorContact: po.vendor?.contact ?? null,
    siteName: po.site?.name ?? null,
    orderedAt: po.orderedAt,
    expectedDay: po.expectedDay,
    orderedBy: po.orderedBy?.name ?? null,
    eventLabel: po.event ? [po.event.code, po.event.name].filter(Boolean).join(' ') || null : null,
    serviceTicket: po.serviceTicket?.docNo ?? null,
    notes: po.notes,
    lines: record.lines.map((line) => ({
      code: line.item?.code ?? null,
      name: purchaseLineName(line),
      description: line.description || null,
      qty: line.qty,
      receivedQty: line.receivedQty,
      unitCost: line.unitCost,
      extended: line.extended,
      use: purchaseLineUseLabel(line),
      cancelled: line.status === 'cancelled',
    })),
    total: po.totalAmount ?? record.lines.reduce((sum, line) => sum + (line.status === 'cancelled' ? 0 : line.extended ?? 0), 0),
  };
}
